import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import SEO from "../components/SEO";
import IdleTimeout from "../components/IdleTimeout";
import { LogOut, ShieldCheck } from "lucide-react";
import { AdminStats, PhoneConversions } from "./admin/AdminStats";
import { TherapiesTable, ConsultationsTable } from "./admin/AdminTables";
import { ContentDashboard } from "./admin/ContentDashboard";
import { ReviewerInbox } from "./admin/ReviewerInbox";
import { ReadinessDashboard } from "./admin/ReadinessDashboard";
import { TestimonialsAdmin } from "./admin/TestimonialsAdmin";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("therapies");
  const [therapies, setTherapies] = useState([]);
  const [consults, setConsults] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, c, a] = await Promise.all([
        api.get("/admin/therapies"),
        api.get("/admin/consultations").catch(() => ({ data: [] })),
        api.get("/admin/analytics").catch(() => ({ data: null })),
      ]);
      setTherapies(t.data);
      setConsults(c.data);
      setAnalytics(a.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = useCallback(async (slug, kind) => {
    try {
      await api.post(`/admin/therapies/${slug}/approve?kind=${kind}`);
      toast.success(`${kind} approval granted`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    }
  }, [load]);

  const toggleVis = useCallback(async (slug) => {
    try {
      await api.post(`/admin/therapies/${slug}/toggle-visibility`);
      toast.success("Visibility updated");
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Cannot toggle visibility");
    }
  }, [load]);

  const handleLogout = useCallback(async () => {
    await logout();
    nav("/admin/login");
  }, [logout, nav]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-stone-50">
      <SEO title="Admin Dashboard" description="VidaVital admin dashboard." path="/admin" noindex />
      <IdleTimeout onLogout={handleLogout} />
      <header className="bg-white border-b border-stone-200">
        <div className="vv-container flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-[color:var(--vv-teal)]" />
            <div>
              <div className="font-display text-lg">VidaVital Admin</div>
              <div className="text-xs text-stone-500">{user?.email} · {user?.role}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-stone-600 hover:text-stone-900">View site →</Link>
            <button data-testid="admin-logout" onClick={handleLogout} className="vv-btn-outline text-sm"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="vv-container py-10">
        {analytics && (
          <>
            <AdminStats analytics={analytics} />
            <PhoneConversions analytics={analytics} />
          </>
        )}

        <div className="flex gap-2 mb-6 flex-wrap">
          {["therapies", "consultations", "review", "content", "testimonials", "readiness", "references"].map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => t === "references" ? nav("/admin/references") : setTab(t)} className={`px-4 py-2 rounded-full text-sm font-medium ${tab === t ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-700"}`}>
              {t === "therapies" ? "Therapies"
               : t === "consultations" ? "Consultation Requests"
               : t === "review" ? "Review Queue"
               : t === "content" ? "CMS Content"
               : t === "testimonials" ? "Testimonials"
               : t === "readiness" ? "AI/SEO Readiness"
               : "References Library"}
            </button>
          ))}
        </div>

        {tab === "therapies" && (
          <TherapiesTable
            therapies={therapies}
            onApprove={approve}
            onToggleVis={toggleVis}
            onRefresh={load}
          />
        )}

        {tab === "consultations" && <ConsultationsTable consults={consults} />}

        {tab === "review" && <ReviewerInbox />}

        {tab === "content" && <ContentDashboard />}

        {tab === "testimonials" && <TestimonialsAdmin />}

        {tab === "readiness" && <ReadinessDashboard />}
      </div>
    </div>
  );
}
